/**
 * AppLayout Component
 *
 * Main application layout using SmartFace SqwLayout
 * Provides header with breadcrumbs, sidebar navigation, and content area
 */

import ApartmentSvg from '@material-symbols/svg-400/rounded/apartment.svg?react'
import DashboardSvg from '@material-symbols/svg-400/rounded/dashboard.svg?react'
import GroupSvg from '@material-symbols/svg-400/rounded/group.svg?react'
import ToggleOnSvg from '@material-symbols/svg-400/rounded/toggle_on.svg?react'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { MaterialSymbol } from '@hrworks/custom'
import { Breadcrumb } from '@hrworks/sui-core/Breadcrumb'
import { BreadcrumbItem } from '@hrworks/sui-core/Breadcrumb/BreadcrumbItem'
import { CollapsibleMenu, CollapsibleMenuEntry } from '@hrworks/sui-core/CollapsibleMenu'
import { Logo } from '@hrworks/sui-core/Logo'
import { SqwLayout } from '@hrworks/sui-extension/SqwLayout'

import { ROUTES } from '../../../routes/routes'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { publicAssetUrl } from '../../utils'
import { UserMenu } from '../UserMenu'

// SqwLayout's sidebar clamps entry icons to 18px wide, which is the box the vendored
// CDN icon component rendered into. MaterialSymbol draws a fixed pixel box instead of
// scaling with the 24px row font size, so pin the height to that same 18px.
const NAV_ICON_SIZE = 18

export const AppLayout = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const breadcrumbs = useBreadcrumbs()
  const { formatMessage } = useIntl()

  /** Navigate via React Router, or open in new tab when Cmd/Ctrl is held */
  const navigateTo = useCallback(
    (path: string) => (e?: { metaKey?: boolean; ctrlKey?: boolean }) => {
      if (e && (e.metaKey || e.ctrlKey)) {
        window.open(path, '_blank', 'noopener,noreferrer')
      } else {
        void navigate(path)
      }
    },
    [navigate],
  )

  // Determine active menu item based on current route
  const activeEntryId = pathname.startsWith('/user-accounts')
    ? 'users'
    : pathname.startsWith('/tenants')
      ? 'tenants'
      : pathname.startsWith('/feature-toggles')
        ? 'feature-toggles'
        : 'dashboard'

  return (
    <SqwLayout
      header={{
        logo: <Logo src={publicAssetUrl('hrworks-logo.svg')} alt="HRWorks Logo" onClick={navigateTo(ROUTES.DASHBOARD)} />,
        leftItems: breadcrumbs.length > 0 ? (
          <Breadcrumb>
            {breadcrumbs.map((item, index) => {
              const isLast = index === breadcrumbs.length - 1
              return (
                <BreadcrumbItem
                  key={item.path}
                  onClick={isLast ? undefined : navigateTo(item.path)}
                >
                  {item.label}
                </BreadcrumbItem>
              )
            })}
          </Breadcrumb>
        ) : undefined,
        rightItems: <UserMenu />,
      }}
      sidebar={{
        children: (
          <nav aria-label={formatMessage({ id: 'nav.primary.aria' })}>
            <CollapsibleMenu activeEntryId={activeEntryId}>
              <CollapsibleMenuEntry
                icon={<MaterialSymbol icon={DashboardSvg} size={NAV_ICON_SIZE} />}
                text={formatMessage({ id: 'nav.dashboard' })}
                id="dashboard"
                onClick={navigateTo(ROUTES.DASHBOARD)}
              />
              <CollapsibleMenuEntry
                icon={<MaterialSymbol icon={ApartmentSvg} size={NAV_ICON_SIZE} />}
                text={formatMessage({ id: 'nav.tenants' })}
                id="tenants"
                onClick={navigateTo(ROUTES.TENANTS)}
              />
              <CollapsibleMenuEntry
                icon={<MaterialSymbol icon={GroupSvg} size={NAV_ICON_SIZE} />}
                text={formatMessage({ id: 'nav.users' })}
                id="users"
                onClick={navigateTo(ROUTES.USERS)}
              />
              <CollapsibleMenuEntry
                icon={<MaterialSymbol icon={ToggleOnSvg} size={NAV_ICON_SIZE} />}
                text={formatMessage({ id: 'nav.featureToggles' })}
                id="feature-toggles"
                onClick={navigateTo(ROUTES.FEATURE_TOGGLES)}
              />
            </CollapsibleMenu>
          </nav>
        ),
      }}
      content={{
        children: (
          <main>
            <Outlet />
          </main>
        ),
      }}
    />
  )
}
